import { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, Timestamp } from 'firebase/firestore';
import Navbar from '../components/Navbar';
import { useUser } from '../context/UserContext';
import GameConfigForm from '../components/GameConfigForm';
import { initializeSnakeGame } from '../firebase/functions/initializeGames';
import { auth } from '../firebase/config';

interface GameConfig {
  id: string;
  title: string;
  description: string;
  url: string;
  imageUrl: string;
  isActive: boolean;
  addedDate: Date;
  requiredTokens?: number;
  category?: string;
}

interface GameFormData extends Omit<GameConfig, 'id' | 'addedDate'> {}

interface GameStats {
  totalPlays: number;
  averageScore: number;
  tokensAwarded: number;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  totalTokensAwarded: number;
}

interface UserData {
  id: string;
  email: string;
  displayName?: string;
  tokens: number;
  gamesPlayed: number;
  highScores: {
    [key: string]: number;
  };
  lastPlayed?: Date;
  isAdmin?: boolean;
}

const AdminDashboard = () => {
  const { userName, stats } = useUser();
  const [games, setGames] = useState<GameConfig[]>([]);
  const [isAddingGame, setIsAddingGame] = useState(false);
  const [editingGame, setEditingGame] = useState<GameConfig | null>(null);
  const [formData, setFormData] = useState<GameFormData>({
    title: '',
    description: '',
    url: '',
    imageUrl: '',
    isActive: true,
    requiredTokens: 0,
    category: 'action'
  });
  const [gameStats, setGameStats] = useState<{[key: string]: GameStats}>({});
  const [userStats, setUserStats] = useState<UserStats>({
    totalUsers: 0,
    activeUsers: 0,
    totalTokensAwarded: 0
  });
  const [users, setUsers] = useState<UserData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch games
  useEffect(() => {
    const fetchGames = async () => {
      const gamesRef = collection(db, 'games');
      const snapshot = await getDocs(gamesRef);
      const gamesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        addedDate: doc.data().addedDate.toDate()
      })) as GameConfig[];
      setGames(gamesList);
    };

    fetchGames();
  }, []);

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      const stats: {[key: string]: GameStats} = {};
      let totalTokens = 0;
      let activeUsers = 0;
      
      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        totalTokens += userData.tokens || 0;
        
        // Count active users (played in last 30 days)
        const lastPlayed = userData.lastPlayed?.toDate();
        if (lastPlayed && (new Date().getTime() - lastPlayed.getTime()) < 30 * 24 * 60 * 60 * 1000) {
          activeUsers++;
        }
        
        // Aggregate game stats
        Object.entries(userData.highScores || {}).forEach(([gameId, score]) => {
          if (!stats[gameId]) {
            stats[gameId] = {
              totalPlays: 0,
              averageScore: 0,
              tokensAwarded: 0
            };
          }
          stats[gameId].totalPlays++;
          stats[gameId].averageScore = (stats[gameId].averageScore * (stats[gameId].totalPlays - 1) + Number(score)) / stats[gameId].totalPlays;
        });
      });

      setGameStats(stats);
      setUserStats({
        totalUsers: usersSnapshot.size,
        activeUsers,
        totalTokensAwarded: totalTokens
      });
    };

    fetchStats();
  }, []);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const usersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastPlayed: doc.data().lastPlayed?.toDate()
      })) as UserData[];
      setUsers(usersList);
    };

    fetchUsers();
  }, []);

  // Handle form submission
  const handleSubmit = async (data: GameFormData) => {
    try {
      if (editingGame) {
        // Update existing game
        await updateDoc(doc(db, 'games', editingGame.id), {
          ...data,
          updatedAt: Timestamp.now()
        });
      } else {
        // Add new game
        await addDoc(collection(db, 'games'), {
          ...data,
          addedDate: Timestamp.now()
        });
      }

      // Reset form and refresh games
      setFormData({
        title: '',
        description: '',
        url: '',
        imageUrl: '',
        isActive: true,
        requiredTokens: 0,
        category: 'action'
      });
      setIsAddingGame(false);
      setEditingGame(null);
      
      // Refresh games list
      const gamesRef = collection(db, 'games');
      const snapshot = await getDocs(gamesRef);
      const gamesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        addedDate: doc.data().addedDate.toDate()
      })) as GameConfig[];
      setGames(gamesList);
    } catch (error) {
      console.error('Error saving game:', error);
    }
  };

  // Handle game deletion
  const handleDelete = async (gameId: string) => {
    if (window.confirm('Are you sure you want to delete this game?')) {
      try {
        await deleteDoc(doc(db, 'games', gameId));
        setGames(games.filter(game => game.id !== gameId));
      } catch (error) {
        console.error('Error deleting game:', error);
      }
    }
  };

  // Add this function near your other handlers
  const initializeDefaultGames = async () => {
    try {
      if (!auth.currentUser) {
        alert('Please log in first');
        return;
      }
      console.log('Starting initialization as:', auth.currentUser.email);
      await initializeSnakeGame();
      console.log('Snake game initialized');

      // Refresh games list after initialization
      const gamesRef = collection(db, 'games');
      const snapshot = await getDocs(gamesRef);
      const gamesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        addedDate: doc.data().addedDate.toDate()
      })) as GameConfig[];
      console.log('Games list refreshed:', gamesList);
      setGames(gamesList);
    } catch (error) {
      console.error('Error initializing games:', error);
      // Optionally add a user-visible error message
      alert('Error initializing games. Check console for details.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <Navbar userName={userName} displayName={stats?.displayName} />
      <div className="max-w-7xl mx-auto p-8">
        <h1 className="text-3xl font-bold mb-8">Game Hub Admin</h1>
        
        {/* Game Management Section */}
        <section className="bg-slate-800 rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Manage Games</h2>
            <div className="flex gap-2">
              <button 
                className="bg-indigo-500 px-4 py-2 rounded hover:bg-indigo-600"
                onClick={initializeDefaultGames}
              >
                Initialize Default Games
              </button>
              <button 
                className="bg-green-500 px-4 py-2 rounded hover:bg-green-600"
                onClick={() => setIsAddingGame(true)}
              >
                Add New Game
              </button>
            </div>
          </div>

          {/* Game Form */}
          {(isAddingGame || editingGame) && (
            <GameConfigForm
              initialData={editingGame ? {
                title: editingGame.title,
                description: editingGame.description,
                url: editingGame.url,
                imageUrl: editingGame.imageUrl,
                isActive: editingGame.isActive,
                requiredTokens: editingGame.requiredTokens || 0,
                category: editingGame.category || 'action'
              } : undefined}
              onSubmit={handleSubmit}
              onCancel={() => {
                setIsAddingGame(false);
                setEditingGame(null);
              }}
              isEditing={!!editingGame}
            />
          )}
          
          {/* Game List */}
          <div className="space-y-4">
            {games.map(game => (
              <div key={game.id} className="flex items-center justify-between bg-slate-700 p-4 rounded">
                <div>
                  <h3 className="font-bold">{game.title}</h3>
                  <p className="text-sm text-gray-400">{game.url}</p>
                  <div className="flex gap-2 mt-1">
                    <span className={`text-xs px-2 py-1 rounded ${game.isActive ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                      {game.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-300">
                      {game.category}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => {
                      setEditingGame(game);
                      setFormData({
                        title: game.title,
                        description: game.description,
                        url: game.url,
                        imageUrl: game.imageUrl,
                        isActive: game.isActive,
                        requiredTokens: game.requiredTokens || 0,
                        category: game.category || 'action'
                      });
                    }}
                    className="bg-blue-500 px-3 py-1 rounded hover:bg-blue-600"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDelete(game.id)}
                    className="bg-red-500 px-3 py-1 rounded hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
        
        {/* Statistics Section */}
        <section className="bg-slate-800 rounded-lg p-6">
          <h2 className="text-2xl font-bold mb-6">Statistics</h2>
          
          {/* Platform Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-slate-700 rounded-lg p-4">
              <h3 className="text-gray-400 text-sm mb-2">Total Users</h3>
              <p className="text-3xl font-bold text-indigo-400">{userStats.totalUsers}</p>
            </div>
            <div className="bg-slate-700 rounded-lg p-4">
              <h3 className="text-gray-400 text-sm mb-2">Active Users (30d)</h3>
              <p className="text-3xl font-bold text-green-400">{userStats.activeUsers}</p>
            </div>
            <div className="bg-slate-700 rounded-lg p-4">
              <h3 className="text-gray-400 text-sm mb-2">Total Tokens Awarded</h3>
              <p className="text-3xl font-bold text-yellow-400">{userStats.totalTokensAwarded}</p>
            </div>
          </div>

          {/* Game Stats */}
          <h3 className="text-xl font-bold mb-4">Game Performance</h3>
          <div className="space-y-4">
            {Object.entries(gameStats).map(([gameId, stats]) => (
              <div key={gameId} className="bg-slate-700 p-4 rounded-lg">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="font-bold text-lg capitalize">{gameId}</h4>
                  <span className="text-sm text-gray-400">
                    {stats.totalPlays} {stats.totalPlays === 1 ? 'play' : 'plays'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">Average Score</p>
                    <p className="text-xl font-bold text-indigo-400">
                      {Math.round(stats.averageScore)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Tokens Awarded</p>
                    <p className="text-xl font-bold text-yellow-400">
                      {stats.tokensAwarded}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* User Management Section */}
        <section className="bg-slate-800 rounded-lg p-6 mt-8">
          <h2 className="text-2xl font-bold mb-6">User Management</h2>
          
          {/* Search Bar */}
          <div className="mb-6">
            <input
              type="text"
              placeholder="Search users by email or display name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-700 rounded px-4 py-2 text-white"
            />
          </div>

          {/* Users List */}
          <div className="space-y-4">
            {users
              .filter(user => 
                user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map(user => (
                <div key={user.id} className="bg-slate-700 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-bold text-lg">
                        {user.displayName || user.email}
                        {user.isAdmin && (
                          <span className="ml-2 text-xs bg-yellow-500/20 text-yellow-300 px-2 py-1 rounded">
                            Admin
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-400">{user.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-400">Tokens</p>
                      <p className="text-xl font-bold text-yellow-400">{user.tokens}</p>
                    </div>
                  </div>

                  {/* Games Played */}
                  <div className="mt-4">
                    <h4 className="font-semibold mb-2">Game History</h4>
                    {Object.entries(user.highScores || {}).length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(user.highScores || {}).map(([game, score]) => (
                          <div key={game} className="bg-slate-600 p-3 rounded">
                            <div className="flex justify-between items-center">
                              <span className="capitalize">{game}</span>
                              <span className="font-bold text-indigo-400">{score}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">No games played yet</p>
                    )}
                  </div>

                  {/* Last Played */}
                  {user.lastPlayed && (
                    <p className="text-sm text-gray-400 mt-4">
                      Last played: {user.lastPlayed.toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard; 